import {
  ChartHeater,
  CustomChangeWrapper,
  CustomText,
  CustomTextHeader,
  HeadWrapper,
  HeaderStatistic,
  PolarityNumberStyled,
  StyledDetailsText,
  StyledHeaderWrapper,
  TourWrapper,
  Wrapper,
} from './styled'
import React, { useState } from 'react'
import { useTextTypes, useWindowSize } from 'hooks'

import Big from 'big.js'
import { ChartData, StateConfig } from '../config'
import { InfoLabelItem } from 'utils/product-service'
import { IntervalSelection } from '../interval-selection'
import { PortfolioChart } from './chart'
import { TooltipWrapper } from 'components/tooltip/components/tooltip-wrapper'
import { currencyFormat } from '../../../utils/formatting-utils'

import { ALL_PORTFOLIO_ID } from 'app-constants'
import { PortfolioStorage } from 'pages/portfolio/storage/interface'
import { assertMultiply } from 'utils'
import moment from 'moment'

interface ToolTipMedia {
  title: string
  content: string
}
interface Props {
  className?: string
  valueHeader?: string
  insidePadding?: number
  chartData: ChartData
  interval: StateConfig
  savaxWeight?: string
  tokensHeld?: InfoLabelItem
  tooltips?: {
    investmentCap?: ToolTipMedia
    investmentValue?: ToolTipMedia
    averagePrice?: ToolTipMedia
    tokensHeld?: ToolTipMedia
    dayPrice?: ToolTipMedia
  }
  portfolioStorage: PortfolioStorage
}

const getCurrentPrice = (price: Big) => {
  if (price.gt(0)) {
    return currencyFormat(price)
  } else {
    return '$0.00'
  }
}
/**
 * 
 *export interface InfoLabelItem {
  name: string
  value: string
  isPercent?: boolean
  isCurrency?: boolean
}
 * @returns 
 */
export const PortfolioChartComponent: React.FC<Props> = ({
  chartData,
  className,
  valueHeader,
  tooltips,
  portfolioStorage,
  interval,
}) => {
  const { selectedProductId: id } = portfolioStorage

  const { valueSize } = useTextTypes()
  const [dataIndex, setDataIndex] = useState(0)
  const [isMouseOut, setMouseOut] = useState(true)
  const { isMobile, isTablet } = useWindowSize()

  const filterUserProductHistory = (): ChartData => {
    const startWindow = moment().unix() - interval.range

    return {
      ...chartData,
      arrTimeline: chartData.arrTimeline.filter((el) => {
        return Big(el.x).gt(startWindow)
      }),
    }
  }
  const filteredChartData = filterUserProductHistory()

  const lastItem = filteredChartData.arrTimeline[filteredChartData.arrTimeline.length - 1]

  const hoverCapital: Big = filteredChartData.arrTimeline[dataIndex]
    ? Big(filteredChartData.arrTimeline[dataIndex].investedCapital)
    : Big(0)
  const hoverInvestment: Big = filteredChartData.arrTimeline[dataIndex]
    ? Big(filteredChartData.arrTimeline[dataIndex].y || '0')
    : Big(0)
  const hoverTokenHeld: Big = filteredChartData.arrTimeline[dataIndex]
    ? Big(filteredChartData.arrTimeline[dataIndex]?.tokenHeld || '0')
    : Big(0)

  const avgHoverPrice: Big = filteredChartData.arrTimeline[dataIndex]
    ? Big(filteredChartData.arrTimeline[dataIndex].avgPrice)
    : Big(0)

  const inMomentHoverPrice: Big = filteredChartData.arrTimeline[dataIndex]
    ? Big(filteredChartData.arrTimeline[dataIndex].price)
    : Big(0)

  const getProcessedValue = (investedCapital: Big, investedValue: Big) => {
    if (investedValue.eq(0) || investedCapital.eq(0)) {
      return Big(0)
    }
    assertMultiply(investedValue.div(investedCapital).minus(1), 100)
    return investedValue.div(investedCapital).minus(1).mul(100)
  }

  const mainValues = {
    investmentCap: isMouseOut && lastItem ? Big(lastItem.investedCapital) : hoverCapital,
    investmentValue: isMouseOut && lastItem && lastItem.y ? Big(lastItem.y) : hoverInvestment,
    averagePrice: isMouseOut && lastItem ? Big(lastItem.avgPrice) : avgHoverPrice,
    dayPrice: isMouseOut && lastItem ? Big(lastItem.price) : inMomentHoverPrice,
    tokens: isMouseOut && lastItem ? lastItem.tokenHeld ?? '0' : hoverTokenHeld,
  }

  const isSmall = isMobile || isTablet
  return (
    <TourWrapper id="tour-chart">
      <Wrapper className={className}>
        <StyledHeaderWrapper>
          <ChartHeater
            id="tour-investment-metrics"
            plateFormate={id.length > 0}
            isPortfolio={false}
          >
            <HeadWrapper>
              <>
                <TooltipWrapper {...tooltips?.investmentCap}>
                  <HeaderStatistic>
                    <CustomTextHeader type={'small'}>{valueHeader || 'Price'}</CustomTextHeader>
                    <StyledDetailsText type={valueSize}>
                      {getCurrentPrice(mainValues.investmentCap)}
                    </StyledDetailsText>
                  </HeaderStatistic>
                </TooltipWrapper>
                <TooltipWrapper {...tooltips?.investmentValue}>
                  <HeaderStatistic>
                    <CustomTextHeader type={'small'}>{'Investment value'}</CustomTextHeader>
                    <CustomChangeWrapper>
                      <StyledDetailsText type={valueSize}>
                        {getCurrentPrice(mainValues.investmentValue)}
                      </StyledDetailsText>

                      <PolarityNumberStyled
                        processedValue={
                          +getProcessedValue(
                            mainValues.investmentCap,
                            mainValues.investmentValue,
                          ).precision(2)
                        }
                        type={'percent'}
                        textType={'p'}
                        suffix={'%'}
                        prefix={
                          //todo clean up prefixes
                          getProcessedValue(
                            mainValues.investmentCap,
                            mainValues.investmentValue,
                          ).gt(0)
                            ? '+'
                            : getProcessedValue(
                                  mainValues.investmentCap,
                                  mainValues.investmentValue,
                                ).lt(0) &&
                                !getProcessedValue(
                                  mainValues.investmentCap,
                                  mainValues.investmentValue,
                                )
                                  .precision(2)
                                  .eq(0)
                              ? '–'
                              : ''
                        }
                      />
                    </CustomChangeWrapper>
                  </HeaderStatistic>
                </TooltipWrapper>
              </>

              {id !== ALL_PORTFOLIO_ID && (
                <TooltipWrapper {...tooltips?.averagePrice}>
                  <HeaderStatistic>
                    <CustomText type={'small'}>{'Average price'}</CustomText>
                    <StyledDetailsText type={valueSize}>
                      {getCurrentPrice(mainValues.averagePrice)}
                    </StyledDetailsText>
                  </HeaderStatistic>
                </TooltipWrapper>
              )}
              {/* {id !== ALL_PORTFOLIO_ID && (
                <TooltipWrapper {...tooltips?.dayPrice}>
                  <HeaderStatistic>
                    <CustomText type={'small'}>{'Day Price'}</CustomText>
                    <StyledDetailsText type={valueSize}>
                      {getCurrentPrice(mainValues.dayPrice)}
                    </StyledDetailsText>
                  </HeaderStatistic>
                </TooltipWrapper>
              )} */}
              {id !== ALL_PORTFOLIO_ID && (
                <TooltipWrapper {...tooltips?.tokensHeld}>
                  <HeaderStatistic>
                    <CustomText type={'small'}>Tokens held</CustomText>
                    <StyledDetailsText type={valueSize}>
                      {Big(mainValues.tokens).precision(3).toString()}
                    </StyledDetailsText>
                  </HeaderStatistic>
                </TooltipWrapper>
              )}
            </HeadWrapper>{' '}
            {!isSmall && <IntervalSelection portfolioStorage={portfolioStorage} />}
          </ChartHeater>
        </StyledHeaderWrapper>
        <PortfolioChart
          chartData={filteredChartData.arrTimeline}
          indexCallback={setDataIndex}
          mouseOut={setMouseOut}
          padding={10}
        />
      </Wrapper>
      {isSmall && <IntervalSelection portfolioStorage={portfolioStorage} />}
    </TourWrapper>
  )
}
